import { useCallback, useEffect, useState } from "react"

// Per-device, per-board preference. The key is suffixed with a scope id
// (`property:<id>` / `project:<id>`) so each board remembers its own choice.
const KEY_PREFIX = "zplanem:tasksViewMode:"

const VIEW_MODES = ["kanban", "list"] as const
export type TasksViewMode = (typeof VIEW_MODES)[number]

// Kanban is today's board — keep it as the first-visit default (least surprising).
const DEFAULT_MODE: TasksViewMode = "kanban"

function isValidMode(value: string | null): value is TasksViewMode {
	return value !== null && (VIEW_MODES as readonly string[]).includes(value)
}

function readStored(storageKey: string): TasksViewMode {
	if (typeof window === "undefined") return DEFAULT_MODE
	try {
		const value = window.localStorage.getItem(storageKey)
		return isValidMode(value) ? value : DEFAULT_MODE
	} catch {
		return DEFAULT_MODE
	}
}

function writeStored(storageKey: string, mode: TasksViewMode) {
	if (typeof window === "undefined") return
	try {
		window.localStorage.setItem(storageKey, mode)
	} catch {
		// localStorage unavailable (private mode, quota) — silently drop
	}
}

/**
 * Persisted Kanban|List view preference for a single Tasks board, modelled on
 * `useLastPropertyId` (lazy init + write-through + cross-tab `storage` sync) but
 * validated against a closed enum. `scopeKey` is the board identity (e.g.
 * `property:<id>` or `project:<id>`) so the choice is remembered per board.
 */
export function useTasksViewMode(scopeKey: string) {
	const storageKey = `${KEY_PREFIX}${scopeKey}`
	const [viewMode, setViewModeState] = useState<TasksViewMode>(() =>
		readStored(storageKey),
	)

	// Re-read when the board (scopeKey) changes — navigating between properties
	// or projects must surface that board's own stored preference.
	useEffect(() => {
		setViewModeState(readStored(storageKey))
	}, [storageKey])

	const setViewMode = useCallback(
		(mode: TasksViewMode) => {
			writeStored(storageKey, mode)
			setViewModeState(mode)
		},
		[storageKey],
	)

	// Keep in sync across tabs.
	useEffect(() => {
		if (typeof window === "undefined") return
		const onStorage = (event: StorageEvent) => {
			if (event.key === storageKey && isValidMode(event.newValue)) {
				setViewModeState(event.newValue)
			}
		}
		window.addEventListener("storage", onStorage)
		return () => window.removeEventListener("storage", onStorage)
	}, [storageKey])

	return { viewMode, setViewMode }
}
